<script lang="ts" setup>
import {
   ContractTemplate,
   ContractTemplateDTO,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import GenericList from '@/core/components/templates/generic-list/GenericList.vue';
import { useGenericList } from '@/core/hooks/useGenericList';
import { useStore } from '@/core/hooks/useStore';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import ContractTemplateWizard from '../contract-template-wizard/ContractTemplateWizard.vue';

const { t, locale } = useI18n();
const { state, dispatch } = useStore();
const route = useRoute();
const customerId = route.params.id;

const contractTemplatesOverview = computed(() => state.customer?.contractTemplate?.contractTemplatesOverview);

const { loading, loadingCreate, loadingChange, deleteData, deleteMultipleData, createData, changeData } = useGenericList<
   ContractTemplate,
   ContractTemplateDTO
>({
   routeName: 'ContractAndPerformanceDataTemplates',
   translation: (length) => t('customers.contract-templates.contract-template', length || 1),
   actions: {
      getAll: (query) => {
         return dispatch('customer/contractTemplate/getContractTemplates', {
            customerId,
            query,
            embed: 'defaultCommericalAgreement',
            append: 'scopes',
         });
      },
      create: (contractTemplate) => {
         return dispatch('customer/contractTemplate/createContractTemplate', { customerId, contractTemplate });
      },
      change: (contractTemplate) => {
         return dispatch('customer/contractTemplate/changeContractTemplate', {
            customerId,
            contractTemplateId: contractTemplate.id,
            contractTemplate,
         });
      },
      delete: (contractTemplateId) => {
         return dispatch('customer/contractTemplate/deleteContractTemplate', { customerId, contractTemplateId });
      },
   },
});
</script>

<template>
   <GenericList
      :class="'contract-and-performance-data-templates'"
      :translation="(length) => t('customers.contract-templates.contract-template', length)"
      :list-items="contractTemplatesOverview?.data || []"
      :meta="contractTemplatesOverview"
      :name="'contract-templates'"
      :mode="'light'"
      :loading="loading"
      :headings="[
         { label: t('generic.name'), style: { minWidth: '19.23rem' } },
         { label: t('employers.employer'), style: { minWidth: '19.23rem' } },
         { label: t('contracts.job-function'), style: { minWidth: '19.23rem' } },
         { label: t('commercial-agreements.commercial-agreement'), style: { width: '100%' } },
         { style: { minWidth: '2rem' } },
      ]"
   >
      <!-- Contract Template List -->
      <template #listItem="{ item }: { item: ContractTemplate }">
         <td>{{ item.name || '-' }}</td>
         <td>{{ item?.employer_company_joint_committee?.company?.name || '-' }}</td>
         <td>
            {{
               item?.contract_template_fields?.find((ctf) => ctf.contract_template_field_name?.key === 'job_function_id')?.selected_value
                  ?.preferred_label?.[locale] || '-'
            }}
         </td>
         <td>
            <div v-if="item.default_commercial_agreement">
               <span class="me-1">{{ item.default_commercial_agreement.performance_code?.name?.[locale] }}</span>
               <span class="me-1">{{ item.default_commercial_agreement.job_profile?.name?.[$i18n.locale] }}</span>
               <span class="me-1">
                  {{ item.default_commercial_agreement.coefficient ? +item.default_commercial_agreement.coefficient : '' }}
               </span>
            </div>
            <div v-else>-</div>
         </td>
         <td>
            <NxtIconButton
               v-if="$auth('delete', item.scopes)"
               :name="'trash'"
               :hover-text="$t('actions.delete-type', { type: $t('companies.templates.template') })"
               @on-click="deleteData({ itemToDelete: item })"
            />
         </td>
      </template>

      <!-- Action Bar Actions -->
      <template v-if="$auth('delete', state.permission?.permissions?.customers)" #actionBarActions="{ selectedItems, deselectAllItems }">
         <NxtIconButton
            :name="'trash'"
            :hover-text="$t('actions.delete-type', { type: $t('companies.templates.template', selectedItems.length) })"
            @on-click="deleteMultipleData({ itemsToDelete: selectedItems, done: deselectAllItems })"
         />
      </template>

      <!-- Edit Contract Template -->
      <template #changeModal="{ item, closeChangeItemModal }">
         <ContractTemplateWizard
            :initial-contract-template="item"
            :loading="loadingChange"
            @on-cancel="closeChangeItemModal"
            @on-save="changeData($event, closeChangeItemModal)"
         />
      </template>

      <!-- Add Contract Template -->
      <template v-if="$auth('create', state.permission?.permissions?.customers)" #createModal="{ closeCreateItemModal }">
         <ContractTemplateWizard
            :loading="loadingCreate"
            @on-cancel="closeCreateItemModal"
            @on-save="createData($event, closeCreateItemModal)"
         />
      </template>
   </GenericList>
</template>
